import gsap from "gsap";
import {
  HorizontalRangeSlider,
  VerticalRangeSlider,
} from "./components/range-slider";
import Checkboxes from "./components/checkboxes";
import GermanyMap from "./components/map";
import AgeInput from "./components/age-input";

/* -------------------------------------------------- */
/* 🙂 General */
/* -------------------------------------------------- */

// Set default GSAP animation properties
gsap.defaults({
  duration: 0.25,
  ease: "power1.inOut",
});

// Check if user prefers reduced motion
const isReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Check if the viewport is desktop-sized
const isDesktop = () => window.matchMedia("(min-width: 1000px)").matches;

/* -------------------------------------------------- */
/* 👵 Check for old browsers */
/* -------------------------------------------------- */

const showOldBrowserBanner = () => {
  if (
    typeof CSSLayerBlockRule === "function" ||
    localStorage.getItem("oldBrowserBanner")
  )
    return;

  const banner = document.createElement("a");
  banner.style =
    "position:fixed;top:0;left:0;width:100%;padding:1rem;background:red;text-align:center;color:#fff;z-index:9999999;cursor:pointer";

  const lang = navigator.language || navigator.userLanguage;
  banner.innerHTML = lang.startsWith("de")
    ? "Sie verwenden einen veralteten Browser. Die Website kann nicht korrekt angezeigt werden."
    : "You are using an outdated browser. The website cannot be displayed correctly.";

  banner.addEventListener("click", () => {
    banner.remove();
    localStorage.setItem("oldBrowserBanner", "dismissed");
  });

  document.body.appendChild(banner);
};

showOldBrowserBanner();

/* -------------------------------------------------- */
/* 📏 Guides */
/* -------------------------------------------------- */

// Create and append guides
const createGuides = () => {
  const guides = document.createElement("div");
  guides.className = "guides wrap";
  guides.innerHTML = `
    <div class="inner grid">
      ${Array(12).fill('<div class="guides__guide"></div>').join("")}
    </div>
  `;
  document.body.appendChild(guides);
  return guides;
};

const guides = createGuides();

// Set up a tween that animates the guides in and out
const toggleGuides = gsap
  .to(guides, {
    duration: 0.25,
    ease: "power2.inOut",
    autoAlpha: 1,
    paused: true,
  })
  .reverse();

// Check if the guides were enabled before
if (localStorage.getItem("debug") === "true") {
  toggleGuides.play().progress(1);
}

// Listen for ⌃G keypresses
document.addEventListener("keydown", (e) => {
  if (e.key === "g" && e.ctrlKey) {
    toggleGuides.reversed() ? toggleGuides.play() : toggleGuides.reverse();
    localStorage.setItem("debug", (!toggleGuides.reversed()).toString());
  }
});

// Update window width data attribute
const updateWindowWidth = () =>
  (guides.dataset.windowWidth = window.innerWidth);
updateWindowWidth();
window.addEventListener("resize", updateWindowWidth);

/* -------------------------------------------------- */
/* 🎚️ Range Slider */
/* -------------------------------------------------- */

const initializeRangeSliders = () => {
  document.querySelectorAll(".range-slider").forEach((rangeSlider) => {
    const horizontalSlider = rangeSlider.querySelector(
      ".horizontal-range-slider"
    );
    const verticalSlider = rangeSlider.querySelector(".vertical-range-slider");

    const options = {};
    if (rangeSlider.dataset.unit !== undefined) {
      options.formatLabel = (value) =>
        `${Math.round(value)}${rangeSlider.dataset.unit}`;
    }

    if (horizontalSlider) new HorizontalRangeSlider(rangeSlider, options);
    if (verticalSlider) new VerticalRangeSlider(rangeSlider, options);
  });
};

initializeRangeSliders();

/* -------------------------------------------------- */
/* ✅ Checkboxes */
/* -------------------------------------------------- */

const initializeCheckboxes = () => {
  const checkboxes = document.querySelectorAll(".checkbox");
  if (checkboxes.length === 0) return;
  new Checkboxes(checkboxes);
};

initializeCheckboxes();

/* -------------------------------------------------- */
/* 🗺️ Map */
/* -------------------------------------------------- */

const initializeMaps = () => {
  document.querySelectorAll(".map").forEach((map) => new GermanyMap(map));
};

initializeMaps();

/* -------------------------------------------------- */
/* 🎂 Age Input */
/* -------------------------------------------------- */

const initializeAgeInputs = () => {
  document.querySelectorAll(".age-input").forEach((age) => new AgeInput(age));
};

initializeAgeInputs();

/* -------------------------------------------------- */
/* 📑 Form Pages */
/* -------------------------------------------------- */

// Submit single-line text fields with Enter
document.addEventListener("keydown", (event) => {
  if (event.key !== "Enter") return;
  if (!event.target.matches("input:is([type=text], [type=email])")) return;
  document.querySelector(".big-button--check")?.click();
});

const initializeFormPage = () => {
  // Check if we're on a form page
  const navigation = document.querySelector(".navigation");
  if (!navigation) return;

  const formPage = navigation.dataset.page;
  const checkButton = document.querySelector(".big-button--check");
  const nextButton = document.querySelector(".big-button--next-page");
  const formFields = document.querySelectorAll(
    "[name]:is(input, select, textarea)"
  );

  let willCheckTheAverage = true;

  // Helper Functions
  const getFormData = (fields) => {
    // Convert form fields to an object
    const formData = {};
    fields.forEach((field) => {
      formData[field.type === "checkbox" ? field.value : field.name] = {
        value: field.type === "checkbox" ? field.checked : field.value,
        type: field.type,
      };
    });
    return formData;
  };

  const sendFormData = async (formData) => {
    // Send form data to the server
    document.documentElement.dataset.loading = true;
    try {
      const response = await fetch(navigation.dataset.api, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ formPage, formData }),
      });
      if (!response.ok) throw new Error("Network response was not ok");
      const data = await response.json();
      if (data.success === true) {
        if (willCheckTheAverage) {
          handleAverages(data.averages);
        } else {
          window.location.href = nextButton.href;
        }
      } else {
        alert("Fehler beim Senden des Formulars.");
      }
    } catch (error) {
      console.error("Error sending form data:", error.message);
    } finally {
      document.documentElement.dataset.loading = false;
    }
  };

  const handleAverages = (averages) => {
    // Process and display average data
    for (const [formField, value] of Object.entries(averages)) {
      const field =
        value.type === "checkbox"
          ? document.querySelector(`[value="${formField}"]`)
          : document.querySelector(`[name="${formField}"]`);
      field.dispatchEvent(
        new CustomEvent("showAverage", {
          detail: { average: value.average, positions: value.positions },
        })
      );
    }
    finishFormPage();
  };

  const finishFormPage = () => {
    // Finish the form page
    document.dispatchEvent(new Event("refreshRangeSlider"));
    const checkButtonHadFocus = document.activeElement === checkButton;
    navigation.classList.add("navigation--finished");
    if (checkButtonHadFocus) nextButton?.focus();
  };

  // Checkbox Handling
  const checkboxes = Array.from(formFields).filter(
    (field) => field.type === "checkbox"
  );
  if (checkboxes.length > 0) {
    const updateCheckboxState = () => {
      const checkedCount = checkboxes.filter((cb) => cb.checked).length;
      checkButton.disabled = checkedCount < 1;
    };
    checkboxes.forEach((cb) =>
      cb.addEventListener("change", updateCheckboxState)
    );
    updateCheckboxState(); // Initial state
  }

  // Skippable Handling: empty age or map submissions navigate directly
  const skippableInput = document.querySelector(
    ".age-input__input, .map__input"
  );
  if (skippableInput) {
    const checkLabel = checkButton.querySelector(".big-button__label");
    const originalLabel = checkLabel.textContent;
    const updateSkipState = () => {
      const empty = skippableInput.value === "";
      willCheckTheAverage = !empty;
      checkLabel.textContent = empty ? "Nächste Frage" : originalLabel;
    };
    skippableInput.addEventListener("input", updateSkipState);
    skippableInput.addEventListener("change", updateSkipState);
    updateSkipState();
  }

  // Radio Handling
  const radios = Array.from(formFields).filter(
    (field) => field.type === "radio"
  );

  if (radios.length > 0) {
    // Disable the check button
    willCheckTheAverage = false;

    // Validate input fields
    const isValidInput = (field) => {
      const value = field.value.trim();
      return field.type === "email"
        ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
        : true;
    };

    // Get fields associated with a radio button
    const getRadioFields = (radio) =>
      radio
        .closest(".contact-option")
        .querySelectorAll(".contact-option__fields [name]");

    // Update form state based on selected radio and its fields
    const updateRadioState = () => {
      const checkedRadio = radios.find((radio) => radio.checked);

      if (!checkedRadio) {
        checkButton.disabled = true;
        return;
      }

      const radioFields = getRadioFields(checkedRadio);
      const allFieldsValid = Array.from(radioFields).every(isValidInput);

      checkButton.disabled = !allFieldsValid;
    };

    // Check initial state and select radio if fields are filled
    const checkInitialState = () => {
      radios.forEach((radio) => {
        const hasFilledFields = Array.from(getRadioFields(radio)).some(
          (field) => field.value.trim() !== ""
        );

        if (hasFilledFields) radio.checked = true;
      });
    };

    // Set up event listeners for radios and their fields
    radios.forEach((radio) => {
      const radioFields = getRadioFields(radio);

      radio.addEventListener("change", () => {
        // Clear and un-require fields for unselected radios
        radios.forEach((r) => {
          if (r !== radio) {
            getRadioFields(r).forEach((field) => {
              field.required = false;
              field.value = "";
            });
          }
        });
        updateRadioState();
      });

      radioFields.forEach((field) => {
        field.addEventListener("input", updateRadioState);
      });
    });

    // Initialize form state
    checkInitialState();
    updateRadioState();
  }

  // Textarea Handling
  const textareas = Array.from(formFields).filter(
    (field) => field.tagName === "TEXTAREA"
  );
  if (textareas.length > 0) {
    // Disable the check button
    willCheckTheAverage = false;

    // const updateTextareaState = () => {
    //   const filledCount = textareas.filter((ta) => ta.value !== "").length;
    //   checkButton.disabled = filledCount !== textareas.length;
    // };
    // textareas.forEach((ta) =>
    //   ta.addEventListener("input", updateTextareaState)
    // );
    // updateTextareaState(); // Initial state
  }

  // Form Submission
  checkButton.addEventListener("click", (e) => {
    e.preventDefault();
    sendFormData(getFormData(formFields));
  });
};

// Initialize the form page
initializeFormPage();

/* -------------------------------------------------- */
/* 🏁 End screen */
/* -------------------------------------------------- */

const restartButton = document.querySelector(".end-screen__restart-button");

const resetSession = async () => {
  try {
    await fetch(document.documentElement.dataset.url + "/reset-session");
  } catch (error) {
    console.error("Error resetting session:", error);
  }
};

if (restartButton) {
  restartButton.addEventListener("click", async (e) => {
    e.preventDefault();
    await resetSession();
    window.location.href = restartButton.href;
  });
}
